import React, { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Panel } from './ApplicantHeader';
import { useHiringApi, errMsg } from './useHiringApi';
import { MESSAGE_TEMPLATES, fmtDateTime } from './hiringConstants';

const newId = () => window.crypto?.randomUUID?.() || `req-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export default function ApplicantMessages({ a, cfg, onUpdated }) {
  const api = useHiringApi();
  const [kind, setKind] = useState('interview_invite');
  const [subject, setSubject] = useState(MESSAGE_TEMPLATES.interview_invite.subject);
  const [body, setBody] = useState(MESSAGE_TEMPLATES.interview_invite.body(a));
  const [reqId, setReqId] = useState(newId);
  const [sending, setSending] = useState(false);

  const pick = (k) => { setKind(k); setSubject(MESSAGE_TEMPLATES[k].subject); setBody(MESSAGE_TEMPLATES[k].body(a)); setReqId(newId()); };

  const send = async (force = false) => {
    if (!a.email) { toast.error('Applicant has no email on file'); return; }
    setSending(true);
    try {
      const doc = await api.message(a.id, { kind, subject, body, client_request_id: reqId, force });
      onUpdated(doc);
      const last = doc.messages?.[doc.messages.length - 1];
      if (last?.status === 'sent') toast.success('Email sent'); else toast.error(`Email ${last?.status?.replace('_', ' ')}: ${last?.error || ''}`);
      setReqId(newId());
    } catch (e) {
      if (e.response?.status === 409 && window.confirm(`${errMsg(e)}\n\nSend it again anyway?`)) { await send(true); return; }
      toast.error(errMsg(e));
    } finally { setSending(false); }
  };

  return (
    <Panel title="Email the applicant" testId="messages-panel">
      {cfg && !cfg.email_configured && <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1" data-testid="email-not-configured">Email provider is not configured — messages will be logged as not sent.</p>}
      <p className="text-xs text-zinc-500">All hiring emails are staff-triggered. Duplicate interview/decision/offer sends are blocked unless you confirm.</p>
      <div className="grid grid-cols-1 gap-2">
        <Select value={kind} onValueChange={pick}><SelectTrigger data-testid="message-kind"><SelectValue /></SelectTrigger>
          <SelectContent>{Object.entries(MESSAGE_TEMPLATES).map(([k, t]) => <SelectItem key={k} value={k}>{t.label}</SelectItem>)}</SelectContent></Select>
        <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject" data-testid="message-subject" />
        <Textarea rows={6} value={body} onChange={(e) => setBody(e.target.value)} data-testid="message-body" />
        <div className="flex justify-end"><Button className="bg-orange-600 hover:bg-orange-700 text-white" size="sm" disabled={sending || !subject.trim() || !body.trim()} onClick={() => send(false)} data-testid="message-send">{sending ? 'Sending…' : `Send to ${a.email || '—'}`}</Button></div>
      </div>
      {(a.messages || []).length > 0 && (
        <ul className="divide-y divide-zinc-100 text-xs" data-testid="message-log">
          {[...a.messages].reverse().map((m) => (
            <li key={m.id} className="py-2 flex items-start justify-between gap-2">
              <div><p className="font-semibold text-zinc-800">{MESSAGE_TEMPLATES[m.kind]?.label || m.kind} — {m.subject}</p><p className="text-zinc-500">{fmtDateTime(m.sent_at)} · {m.actor_name}</p></div>
              <span className={m.status === 'sent' ? 'text-emerald-700' : 'text-red-700'} title={m.error || ''}>{m.status.replace('_', ' ')}</span>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}
